// Drawing text and image (photo / logo slot) objects on Canvas 2D (M14). Used by the tiles (preview) and by
// the PNG export through paintItem(), so both are the same pixels.
import type { Doc, ImageNode, Subpath, TextNode } from '../model/types';
import { shapeToSubpaths } from '../geometry/bezier';
import { getBitmap } from '../assets';
import { layoutText } from '../text/layout';
import { composite, cssColor, paintStyle, applyStroke } from './paint';

/** The frame of an image slot in its own coordinates (from 0, 0). */
export function framePath(n: ImageNode): Subpath[] {
  const { w, h, shape, radius } = n.frame;
  if (shape === 'ellipse')
    return shapeToSubpaths({ type: 'ellipse', cx: w / 2, cy: h / 2, rx: w / 2, ry: h / 2 });
  const r = shape === 'rounded' ? Math.min(radius, w / 2, h / 2) : 0;
  return shapeToSubpaths({ type: 'rect', x: 0, y: 0, w, h, radii: [r, r, r, r] });
}

/** Where the image is drawn inside its frame (local coordinates): cover / contain × zoom, plus the offset. */
export function imageDrawRect(n: ImageNode): { x: number; y: number; w: number; h: number } {
  const { w, h } = n.frame;
  if (!n.iw || !n.ih) return { x: 0, y: 0, w, h };
  const s =
    (n.fit === 'cover' ? Math.max(w / n.iw, h / n.ih) : Math.min(w / n.iw, h / n.ih)) * Math.max(0.1, n.zoom);
  const dw = n.iw * s,
    dh = n.ih * s;
  return { x: (w - dw) / 2 + n.offsetX * w, y: (h - dh) / 2 + n.offsetY * h, w: dw, h: dh };
}

/** Text: the glyph outlines filled with the text colour (object opacity applies). */
export function drawText(g: CanvasRenderingContext2D, n: TextNode, path: Path2D | null, doc: Doc | null) {
  if (!path || !layoutText(n)?.commands.length) return;
  g.fillStyle = cssColor(n.style.color, doc);
  g.fill(path, 'nonzero');
}

/** An empty photo slot: light grey with a simple picture sign (no text, so it needs no font). */
function placeholder(g: CanvasRenderingContext2D, n: ImageNode) {
  const { w, h } = n.frame;
  g.fillStyle = 'rgba(205,205,205,1)';
  g.fillRect(0, 0, w, h);
  const s = Math.min(w, h) * 0.22;
  const cx = w / 2,
    cy = h / 2;
  g.fillStyle = 'rgba(160,160,160,1)';
  g.beginPath();
  g.moveTo(cx - s, cy + s * 0.6);
  g.lineTo(cx - s * 0.3, cy - s * 0.3);
  g.lineTo(cx + s * 0.1, cy + s * 0.2);
  g.lineTo(cx + s * 0.45, cy - s * 0.1);
  g.lineTo(cx + s, cy + s * 0.6);
  g.closePath();
  g.fill();
  g.beginPath();
  g.arc(cx + s * 0.55, cy - s * 0.55, s * 0.2, 0, Math.PI * 2);
  g.fill();
}

/** Image slot: fills (frame background), the image clipped to the frame, then strokes (frame border). */
export function drawImage(g: CanvasRenderingContext2D, n: ImageNode, path: Path2D | null, doc: Doc | null) {
  if (!path) return;
  // An empty logo slot draws nothing at all (no backdrop): the restaurant may not have a logo.
  if (n.role === 'logo' && !n.assetId) return;
  for (const f of n.appearance.fills) {
    const st = paintStyle(g, f.paint, doc);
    if (!st) continue;
    g.save();
    g.globalAlpha *= f.opacity;
    g.globalCompositeOperation = composite(f.blend);
    g.fillStyle = st;
    g.fill(path);
    g.restore();
  }
  const bmp = n.assetId ? getBitmap(n.assetId) : null;
  g.save();
  g.clip(path);
  if (bmp) {
    const r = imageDrawRect(n);
    g.imageSmoothingQuality = 'high';
    g.drawImage(bmp, r.x, r.y, r.w, r.h);
  } else if (n.role !== 'logo') placeholder(g, n);
  g.restore();
  for (const s of n.appearance.strokes) {
    const st = paintStyle(g, s.paint, doc);
    if (!st || s.width <= 0) continue;
    g.save();
    g.globalAlpha *= s.opacity;
    g.strokeStyle = st;
    applyStroke(g, s);
    g.stroke(path);
    g.restore();
  }
}
